import {
  BaseOutfitItemApiResponse,
  InStockOutfitItemApiResponse,
  Outfit,
  OutfitApiListResponse,
  OutfitApiResponse,
  OutfitItem
} from 'types/outfits';

const transformOutfitItemResponse = (item: InStockOutfitItemApiResponse | BaseOutfitItemApiResponse): OutfitItem => {
  const base = {
    brand: item.brand,
    category: item.category,
    imageId: item.image_id,
    link: item.link,
    productId: item.product_id,
    productType: item.product_type,
    styleId: item.style_id,
    inStock: item.in_stock
  };
  if (item.in_stock) {
    // override brand field w/ brandName since it's more "up to date"
    const { brand_name: brand, product_name: productName, list_price: listPrice, price } = item;
    return { ...base, brand, productName, listPrice, price };
  }
  return base;
};

function isSocks(item: OutfitItem): boolean {
  return item.productType === 'Clothing' && item.category === 'Socks';
}

function isAccessory(item: OutfitItem): boolean {
  return item.productType === 'Accessory' || item.productType === 'Accessories';
}

/**
 * Sort comparator which puts socks/accessories at the end of the list.
 * @param o1 item in an outfit
 * @param o2 item in an outfit
 */
export function accessoryAndSockComparator(o1: OutfitItem, o2: OutfitItem) {
  const o1Smaller = isSocks(o1) || isAccessory(o1);
  const o2Smaller = isSocks(o2) || isAccessory(o2);
  if (o1Smaller && !o2Smaller) {
    return 1;
  } else if (!o1Smaller && o2Smaller) {
    return -1;
  } else {
    // both are socks/accessories or neither are
    return 0;
  }
}

const outfitItemComparatorFactory =
  (sourceStyleId: string) =>
  (o1: OutfitItem, o2: OutfitItem): number => {
    if (o1.inStock && o2.inStock) {
      // if both items are in stock, prioritize other styles than the viewed product
      if (o1.styleId === sourceStyleId) {
        return 1;
      } else if (o2.styleId === sourceStyleId) {
        return -1;
      } else {
        return accessoryAndSockComparator(o1, o2);
      }
    } else if (!o1.inStock && o2.inStock) {
      return 1;
    } else {
      return -1;
    }
  };

export const transformOutfitResponse = (outfitApiResponse: OutfitApiResponse, sourceProductId: string, sourceStyleId: string): Outfit => ({
  id: outfitApiResponse.id,
  curatorName: outfitApiResponse.curator_name,
  items: outfitApiResponse.items.map(transformOutfitItemResponse).sort(outfitItemComparatorFactory(sourceStyleId)),
  sourceProductId,
  sourceStyleId
});

export const sanitizeApiData = (outfitApiResponse: OutfitApiListResponse, sourceProductId: string, sourceStyleId: string): Outfit[] =>
  outfitApiResponse.outfits
    .map(o => transformOutfitResponse(o, sourceProductId, sourceStyleId))
    .filter(o => o.items.length > 1 && o.items.length < 10);

export const shareableLink = (outfit: Outfit) => `/outfit/${outfit.id}?productId=${outfit.sourceProductId}&styleId=${outfit.sourceStyleId}`;
